// Angular Imports
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// project import
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './theme/shared/shared.module';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClientJsonpModule } from '@angular/common/http';
import { BreadcrumbComponent } from './theme/shared/components/breadcrumb/breadcrumb.component';

// third party
import { ToastrModule } from 'ngx-toastr';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';

// bootstrap import
import { NgbDropdownModule, NgbNavModule, NgbTooltipModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { CommonModule } from '@angular/common';
import { ValidateFormDirective } from './shared/directives/validate-form';
import { FormsModule } from '@angular/forms';
import { CaptchaModule } from './captcha/captcha.module';
import { NgxMapboxGLModule } from 'ngx-mapbox-gl';
import { LoaderService } from './shared/loader/loader.service';
import { LoaderInterceptorService } from './shared/loader/loader-interceptor.service';
import { NotifierModule } from 'angular-notifier';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomTranslateLoader } from './theme/shared/custom-translate-loader';

// SIM management (shared library, see frontend/node_modules/sim-management-frontend)
import {
	CAN_WRITE,
	CHART_RENDERER,
	CONFIRM_DIALOG,
	INotificationService,
	NOTIFICATION_SERVICE,
	SCT_TABLE_CONFIG,
	SIM_API_BASE,
	SctTableConfig,
	SimManagementModule,
	TRANSLATE_FN,
	TranslateFn
} from 'sim-management-frontend/dist/sim-management';
import { NotificationMessageService } from './shared/notification-message/notification-message.service';
import { NotificationMessage } from './shared/notification-message/notification-message.model';
import { UsersService } from './dashboard/users/users.service';
import { AccessFunctions } from './theme/shared/access-functions';
import { CommonService } from './shared/services/common.service';
import { DeviceConstantsService } from './dashboard/devices/device-constants.service';
import { buildSimHistoryHeader, buildSimPresets, SIM_TABLE_IDS } from './dashboard/admin/sim-management/sim-management.config';
import { SimUsageChartComponent } from './dashboard/admin/sim-management/sim-usage-chart.component';
import { buildSimConfirmDialog } from './dashboard/admin/sim-management/sim-confirm-dialog';

/**
 * The library never touches `@ngx-translate` itself — two `TranslateModule.forRoot()` calls in
 * one root injector break the host's loader — so it asks the host for a translate function.
 */
export function simTranslateFactory(translate: TranslateService): TranslateFn {
	return (key: string, params?: Record<string, any>) => translate.instant(key, params);
}

/** Without this the library hides enable, disable, lock and unblock. That is its safe default. */
export function simCanWriteFactory(usersService: UsersService) {
	return () => usersService.hasAccessFunctions([AccessFunctions.sim_management]);
}

/**
 * The library calls `setMessage(message, type?, timeout?, scrollToTop?, show?, safeType?)` while
 * VMSPRO's service takes `setMessage(message, options)`. Only the arguments the library actually
 * passed are copied, so the service keeps applying its own defaults for the rest.
 */
export function simNotificationFactory(notificationService: NotificationMessageService): INotificationService {
	return {
		setMessage(message: string, type?: string, timeout?: number, scrollToTop?: boolean, show?: boolean, safeType?: string) {
			const options: NotificationMessage = {};

			if (type !== undefined) options.type = type as NotificationMessage['type'];
			if (timeout !== undefined) options.timeout = timeout;
			if (scrollToTop !== undefined) options.scrollToTop = scrollToTop;
			if (show !== undefined) options.show = show;
			if (safeType !== undefined) options.safeType = safeType;

			notificationService.setMessage(message, options);
		}
	};
}

/** Column presets and table ids. Built here because they need translations and user settings. */
export function simTableConfigFactory(
	translate: TranslateService,
	commonService: CommonService,
	deviceConstants: DeviceConstantsService
): SctTableConfig {
	return {
		presets: buildSimPresets(translate, commonService, deviceConstants),
		tableIds: SIM_TABLE_IDS,
		historyHeader: buildSimHistoryHeader(translate, commonService, deviceConstants),
		// The two averages stay off the table until their "Calculate Daily Average" button is
		// clicked, as on the old screen (VMSPRO-593). The data is already on every row.
		dailyAverageColumns: { 7: 'avg_7d_usage', 30: 'avg_30d_usage' },
		usage_history_type: 'category',
		deviceIdField: 'imei'
	};
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		SharedModule,
		NgbDropdownModule,
		NgbNavModule,
		NgbTooltipModule,
		NgbModule,
		BreadcrumbComponent,
		CaptchaModule,
		ToastrModule.forRoot(),
		BrowserAnimationsModule,
		HttpClientModule,
		ValidateFormDirective,
		// HttpClientXsrfModule.withOptions({//todo check this
		//   cookieName: 'XSRF-TOKEN',
		//   headerName: 'X-XSRF-Token',
		// }),

		NgxMapboxGLModule.withConfig({
			accessToken: 'pk.eyJ1IjoidHNocmVhaCIsImEiOiJja2phZ2dlbTkwc2gzMnluMGRiNXc3NXpzIn0.oRz9X880rKvLMlfzU5jUGg',
			//@ts-ignore
			geocoderAccessToken: 'pk.eyJ1IjoidHNocmVhaCIsImEiOiJja2phZ2dlbTkwc2gzMnluMGRiNXc3NXpzIn0.oRz9X880rKvLMlfzU5jUGg'
		}),
		SweetAlert2Module.forRoot(),
		NgSelectModule,
		CommonModule,
		RouterModule,
		FormsModule,
		HttpClientJsonpModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useClass: CustomTranslateLoader
			}
		}),
		NotifierModule,
		FontAwesomeModule,
		// Plain import, not forRoot(): every piece of config below needs an injected service,
		// which a static forRoot() object cannot reach. A directly provided token wins over
		// forRoot() anyway.
		SimManagementModule
	],
	providers: [
		LoaderService,
		TranslateService,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: LoaderInterceptorService,
			multi: true
		},
		{ provide: SIM_API_BASE, useValue: '/api/v1' },
		{
			provide: TRANSLATE_FN,
			useFactory: simTranslateFactory,
			deps: [TranslateService]
		},
		{
			provide: CAN_WRITE,
			useFactory: simCanWriteFactory,
			deps: [UsersService]
		},
		{
			provide: NOTIFICATION_SERVICE,
			useFactory: simNotificationFactory,
			deps: [NotificationMessageService]
		},
		{ provide: CHART_RENDERER, useValue: SimUsageChartComponent },
		// SweetAlert confirms, as on the old screen, instead of the library's own modal (VMSPRO-593).
		{
			provide: CONFIRM_DIALOG,
			useFactory: buildSimConfirmDialog,
			deps: [TranslateService]
		},
		{
			provide: SCT_TABLE_CONFIG,
			useFactory: simTableConfigFactory,
			deps: [TranslateService, CommonService, DeviceConstantsService]
		}
	],
	bootstrap: [AppComponent]
})
export class AppModule {
	constructor(library: FaIconLibrary) {
		// Add an icon to the library for convenient access in other components
		library.addIconPacks(fas, far, fab);
	}
}
